import { useEffect, useRef, useState } from 'preact/hooks';
import { MaskPainter, type Tool } from './maskPainter';

const BRUSHES = [
  { id: 'S', label: 'Small brush', frac: 0.015 },
  { id: 'M', label: 'Medium brush', frac: 0.035 },
  { id: 'L', label: 'Large brush', frac: 0.07 },
] as const;

export interface MaskEditorState {
  painter: MaskPainter;
  tool: Tool;
  setTool(t: Tool): void;
  brush: number;
  setBrush(i: number): void;
}

/**
 * Editor state for "Fix edges". The painter exists only while editing
 * (null otherwise); `open` starts a new editing session.
 */
export function useMaskEditor() {
  const [, setTick] = useState(0);
  const rerender = () => setTick((t) => t + 1);
  const [painter, setPainter] = useState<MaskPainter | null>(null);
  const [tool, setTool] = useState<Tool>('paint');
  const [brush, setBrush] = useState(1);
  useEffect(() => () => painter?.dispose(), [painter]);

  const open = (start: Uint8Array, detected: Uint8Array, width: number, height: number) => {
    const brand = getComputedStyle(document.documentElement).getPropertyValue('--fv-primary').trim() || '#1f6feb';
    setTool('paint');
    setPainter(new MaskPainter(start, detected, width, height, hexToRgba(brand, 0.5), rerender));
  };
  const close = () => setPainter(null);
  const state: MaskEditorState | null = painter && { painter, tool, setTool, brush, setBrush };
  return { state, open, close };
}

function hexToRgba(hex: string, a: number) {
  const n = parseInt(hex.replace('#', ''), 16);
  return Number.isNaN(n) ? `rgba(31,111,235,${a})` : `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/** Drawing surface laid over the photo. Blocks page scroll while drawing. */
export function MaskOverlay({ state }: { state: MaskEditorState }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const { painter, tool, brush } = state;

  useEffect(() => {
    const c = painter.display;
    c.className = 'absolute inset-0 h-full w-full';
    hostRef.current!.appendChild(c);
    return () => c.remove();
  }, [painter]);

  const toImage = (e: PointerEvent): [number, number] => {
    const r = hostRef.current!.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * painter.width, ((e.clientY - r.top) / r.height) * painter.height];
  };

  return (
    <div
      ref={hostRef}
      data-testid="mask-editor"
      class="absolute inset-0 cursor-crosshair"
      style={{ touchAction: 'none' }}
      onPointerDown={(e) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        painter.begin(tool, BRUSHES[brush].frac * painter.width, ...toImage(e));
      }}
      onPointerMove={(e) => painter.move(...toImage(e))}
      onPointerUp={() => painter.end()}
      onPointerCancel={() => painter.end()}
    />
  );
}

interface ToolbarProps {
  state: MaskEditorState;
  hint?: string | null;
  onDone(mask: Uint8Array): void;
  onCancel(): void;
}

export function MaskToolbar({ state, hint, onDone, onCancel }: ToolbarProps) {
  const { painter, tool, setTool, brush, setBrush } = state;
  const seg = (active: boolean) =>
    `min-h-11 flex-1 rounded-lg px-3 text-sm font-semibold transition ${active ? 'bg-white text-ink shadow-sm' : 'text-muted hover:text-ink'}`;
  return (
    <div class="space-y-3 px-4 pt-3 sm:px-0">
      {hint && <p class="rounded-xl bg-accent/20 px-3 py-2 text-sm font-medium text-ink">{hint}</p>}
      <div class="flex gap-2">
        <div class="flex flex-1 gap-1 rounded-xl bg-paper p-1" role="group" aria-label="Tool">
          <button type="button" aria-pressed={tool === 'paint'} class={seg(tool === 'paint')} onClick={() => setTool('paint')}>
            Paint
          </button>
          <button type="button" aria-pressed={tool === 'erase'} class={seg(tool === 'erase')} onClick={() => setTool('erase')}>
            Erase
          </button>
        </div>
        <div class="flex gap-1 rounded-xl bg-paper p-1" role="group" aria-label="Brush size">
          {BRUSHES.map((b, i) => (
            <button key={b.id} type="button" aria-label={b.label} aria-pressed={brush === i} class={`${seg(brush === i)} grid w-11 flex-none place-items-center px-0`} onClick={() => setBrush(i)}>
              <span class="block rounded-full bg-current" style={{ width: `${6 + i * 5}px`, height: `${6 + i * 5}px` }} />
            </button>
          ))}
        </div>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <button type="button" disabled={!painter.canUndo} onClick={() => painter.undo()} class="min-h-11 rounded-full px-4 text-sm font-medium ring-1 ring-line hover:bg-paper disabled:opacity-40">
          Undo
        </button>
        <button type="button" onClick={() => painter.resetToDetected()} class="min-h-11 rounded-full px-4 text-sm font-medium ring-1 ring-line hover:bg-paper">
          Reset to detected
        </button>
        <span class="flex-1" />
        <button type="button" onClick={onCancel} class="min-h-11 px-3 text-sm font-medium text-muted hover:text-ink">
          Cancel
        </button>
        <button type="button" onClick={() => onDone(painter.result())} class="min-h-11 rounded-full bg-ink px-5 text-sm font-semibold text-white">
          Done
        </button>
      </div>
    </div>
  );
}
